import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Paper } from '@mui/material';
import { CognitoUser, CognitoUserAttribute, CognitoUserPool } from 'amazon-cognito-identity-js';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from 'react-oidc-context';

const Profile: React.FC = () => {
    const { user, setUser } = useUser();
    const auth = useAuth();
    
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    // Loading state for UI indicators
    const [, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    
    // Initialize form with user data from context
    useEffect(() => {
        if (user) {
            setFirstName(user.firstName || '');
            setLastName(user.lastName || '');
        }
    }, [user]);
    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess(false);
        setLoading(true);

        try {
            if (!auth.user?.access_token) {
                throw new Error('You must be logged in to update your profile');
            }
            
            // Create Cognito user pool with the current environment variables
            const userPool = new CognitoUserPool({
                UserPoolId: process.env.REACT_APP_USER_POOL_ID || '',
                ClientId: process.env.REACT_APP_CLIENT_ID || ''
            });
            
            // Create Cognito user
            const cognitoUser = new CognitoUser({
                Username: auth.user.profile.email || auth.user.profile.sub || '',
                Pool: userPool
            });
            
            // Set the session manually with the token from the OIDC context
            cognitoUser.setSignInUserSession({
                getIdToken: () => ({ getJwtToken: () => auth.user?.id_token || '' }),
                getRefreshToken: () => ({ getToken: () => auth.user?.refresh_token || '' }),
                getAccessToken: () => ({ getJwtToken: () => auth.user?.access_token || '' }),
                isValid: () => true
            } as any);

            // Update attributes
            const attributes = [
                new CognitoUserAttribute({ Name: 'given_name', Value: firstName }),
                new CognitoUserAttribute({ Name: 'family_name', Value: lastName })
            ];

            await new Promise<void>((resolve, reject) => {
                cognitoUser.updateAttributes(attributes, (err, result) => {
                    if (err) {
                        console.error('Update attributes error:', err);
                        reject(err);
                    } else {
                        console.log('Update successful:', result);
                        resolve();
                    }
                });
            });

            // Update the user context with the new values
            if (user) {
                const updatedUser = {
                    ...user,
                    firstName,
                    lastName
                };
                setUser(updatedUser);
            }

            setSuccess(true);
        } catch (err) {
            console.error('Profile update error:', err);
            setError(err instanceof Error ? err.message : 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Paper sx={{ p: 3, width: '100%', maxWidth: 400 }}>
                <Typography variant="h6" gutterBottom>Profile Settings</Typography>
                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label="First Name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Last Name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        margin="normal"
                    />
                    {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
                    {success && <Typography color="success.main" sx={{ mt: 2 }}>Profile updated successfully!</Typography>}
                    <Button type="submit" variant="contained" sx={{ mt: 2 }} fullWidth>
                        Update Profile
                    </Button>
                </form>
            </Paper>
        </Box>
    );
};

export default Profile;